import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMenu } from "hook/useMenu";
import UpdateFirmwarePresenter from "./UpdateFirmwarePresenter";
import { useAppSelector } from "redux/hooks";
import { selectStomp } from "redux/stomp/stompSelector";
import {
  FIRMWARE_UPGRADE_STATUS,
  FirmwareUpdateInfoResponse,
  FIRMWARE_UPDATE_STEP,
  FirmwareAliveStatusResponse,
} from "api/interfaces/settingsInterface.interface";
import { useQuery, useQueryClient } from "react-query";
import {
  getFirmwareUpdateInfo,
  postFirmwareAliveStatus,
  putFirmwareUpgrade,
} from "api/settingAPI";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { firmwareErrorKey } from "pages/maintenance/firmware/firmwareDW/add/AddModalContainer";
import { nextStep, RELEASE_NOTE_TYPE, updateStep } from "utils/firmwareUtil";

interface Props {
  recorderId: string;
  onCancelUpdateFirmware: () => void;
  onlineStatus: boolean;
  autoUpdate: boolean;
}

const checkStep = [
  FIRMWARE_UPGRADE_STATUS.VERIFY,
  FIRMWARE_UPGRADE_STATUS.PROGRESS,
  FIRMWARE_UPGRADE_STATUS.START,
];

export const stepToPercent = (step: FIRMWARE_UPGRADE_STATUS): number => {
  return updateStep[step] * 20;
};
export default function UpdateFirmwareContainer({
  recorderId,
  onCancelUpdateFirmware,
  onlineStatus,
  autoUpdate,
}: Props) {
  const { menuNavigation } = useMenu({});
  const queryClient = useQueryClient(); //나중에 웹소켓으로 percent 받아오기
  const [step, setStep] = useState<FIRMWARE_UPDATE_STEP>(
    FIRMWARE_UPDATE_STEP.INTRO
  );
  const [showReleaseNote, setShowReleaseNote] = useState<
    typeof RELEASE_NOTE_TYPE[keyof typeof RELEASE_NOTE_TYPE] | null
  >(null);
  const {
    handleErrorNotify,
    lang,
    createCustomError,
    getErrorMessage,
    intlString,
  } = useApiError();
  const [uploadStep, setUploadStep] = useState<FIRMWARE_UPGRADE_STATUS>(
    FIRMWARE_UPGRADE_STATUS.READY
  );
  const [percent, setPercent] = useState<number>(0);
  const [errorReason, setErrorReason] = useState<string>("");
  const { firmwareUpgrade } = useAppSelector(selectStomp);
  const aliveTimer = useRef<NodeJS.Timeout | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const aliveCountRef = useRef(0);
  const onConfirmModal = () => {
    setShowConfirmModal(!showConfirmModal);
  };
  const onClickManageAutoUpdate = () => {
    menuNavigation({
      to: "/maintenance/firmware",
    });
  };
  const clearAliveTimer = () => {
    if (aliveTimer.current) {
      clearTimeout(aliveTimer.current);
      aliveTimer.current = null;
      aliveCountRef.current = 0;
    }
  };
  const checkAlive = useCallback(() => {
    if (aliveCountRef.current > 2) {
      clearAliveTimer();
      firmwareUpdateInfoQuery.refetch();
      return;
    }
    mutationFirmwareAliveStatus.mutate({
      recorderId,
      retryCount: aliveCountRef.current,
    });
  }, [recorderId]);

  const firmwareUpdateInfoQuery = useQuery(
    ["firmwareUpdateInfo", recorderId],
    () => getFirmwareUpdateInfo(recorderId),
    {
      enabled: !!recorderId,
      onSuccess: (res: FirmwareUpdateInfoResponse) => {
        if (res.error !== 0) {
          handleErrorNotify(
            createCustomError(
              res,
              lang.label.setting.firmware.notify.getInfo.fail
            ),
            lang.label.setting.firmware.notify.getInfo.fail,
            firmwareErrorKey
          );
          return;
        }
        changeStatus(res.result.upgradeStatus as FIRMWARE_UPGRADE_STATUS);
      },
      onError: (err: any) => {
        handleErrorNotify(
          err,
          lang.label.setting.firmware.notify.getInfo.fail,
          firmwareErrorKey
        );
      },
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );
  const mutationFirmwareUpdate = useStatusMutation(putFirmwareUpgrade, {
    onSuccess: (res: FirmwareUpdateInfoResponse) => {
      if (res.error !== 0) {
        handleErrorNotify(
          createCustomError(
            res,
            lang.label.setting.firmware.notify.update.fail
          ),
          lang.label.setting.firmware.notify.update.fail,
          firmwareErrorKey
        );
        return;
      }
      changeStatus(res.result.upgradeStatus as FIRMWARE_UPGRADE_STATUS);
    },
    onError: (err: any) => {
      changeStatus(FIRMWARE_UPGRADE_STATUS.FAIL);
      setErrorReason(
        getErrorMessage(
          err,
          lang.label.setting.firmware.notify.update.fail,
          firmwareErrorKey
        )
      );
    },
  });
  const onClickUpdate = () => {
    if (!mutationFirmwareUpdate.idle) return;
    if (!data?.isUpdate) return;
    setShowConfirmModal(false);
    changeStatus(FIRMWARE_UPGRADE_STATUS.INIT);
    mutationFirmwareUpdate.mutate({
      recorderId,
      payload: {
        targetVersion: data?.updateVersion,
      },
    });
  };
  const onClickCancel = () => {
    onCancelUpdateFirmware();
  };
  const onModal = (
    type: typeof RELEASE_NOTE_TYPE[keyof typeof RELEASE_NOTE_TYPE]
  ) => {
    setShowReleaseNote((prev) => (type === prev ? null : type));
  };
  const mutationFirmwareAliveStatus = useStatusMutation(
    postFirmwareAliveStatus,
    {
      onSuccess: (res: FirmwareAliveStatusResponse) => {
        if (res.error !== 0 || !res.result || !!res.result.marked) {
          clearAliveTimer();
          firmwareUpdateInfoQuery.refetch();
          return;
        }
        if (!res.result.alive) {
          // false 라면 카운트를 +1 한다
          aliveCountRef.current++;
        }
        if (!res.result.marked) {
          aliveTimer.current = setTimeout(checkAlive, 10000);
        }
      },
      onError: () => {
        clearAliveTimer();
        firmwareUpdateInfoQuery.refetch();
      },
    }
  );
  const changeStatus = useCallback(
    (status: FIRMWARE_UPGRADE_STATUS) => {
      if (!status) return;
      if (status !== uploadStep) {
        clearAliveTimer();
        if (checkStep.includes(status)) {
          aliveTimer.current = setTimeout(checkAlive, 10000);
        }
      }
      setUploadStep((prev) => {
        // 되돌아가는 값이면 무시
        if (updateStep[prev] >= updateStep[status]) return prev;
        setStep(nextStep(status));
        if (status !== FIRMWARE_UPGRADE_STATUS.FAIL) {
          setPercent(updateStep[status] * 20);
        }
        return status;
      });
    },
    [checkAlive, uploadStep]
  );
  const data = useMemo(() => {
    return firmwareUpdateInfoQuery.data?.result;
  }, [firmwareUpdateInfoQuery.data]);
  useEffect(() => {
    if (step !== FIRMWARE_UPDATE_STEP.UPDATE) {
      clearAliveTimer();
      return;
    }
    if (
      firmwareUpgrade.recorderId === recorderId &&
      firmwareUpgrade.status &&
      step === FIRMWARE_UPDATE_STEP.UPDATE &&
      uploadStep !== firmwareUpgrade.status
    ) {
      changeStatus(firmwareUpgrade.status ?? FIRMWARE_UPGRADE_STATUS.READY);
      if (
        firmwareUpgrade.status === FIRMWARE_UPGRADE_STATUS.FAIL &&
        firmwareUpgrade.reason
      ) {
        type Errortype = keyof typeof lang.label.setting.firmware.upgradeError;
        const beError: Errortype = (firmwareUpgrade.reason || "").split(
          "("
        )[0] as Errortype;
        const agentError: Errortype = (
          (firmwareUpgrade.reason || "").split("(")[1] || ""
        ).replace(")", "") as Errortype;
        let errorMessage: string =
          lang.label.setting.firmware.errorCode["50022"];

        if (agentError) {
          errorMessage = lang.label.setting.firmware.upgradeError[agentError];
        }
        if (beError) {
          errorMessage = lang.label.setting.firmware.upgradeError[beError];
        }

        setErrorReason(intlString(errorMessage));
      }
      if (
        firmwareUpgrade.status === FIRMWARE_UPGRADE_STATUS.COMPLETE ||
        firmwareUpgrade.status === FIRMWARE_UPGRADE_STATUS.FAIL
      ) {
        queryClient.invalidateQueries("recorder");
      }
    }
  }, [
    firmwareUpgrade,
    queryClient,
    recorderId,
    changeStatus,
    step,
    uploadStep,
    lang,
    intlString,
  ]);
  useEffect(() => {
    if (!onlineStatus) {
      changeStatus(FIRMWARE_UPGRADE_STATUS.FAIL);
    }
  }, [onlineStatus, changeStatus]);

  useEffect(() => {
    return () => {
      clearAliveTimer();
    };
  }, []);
  return (
    <UpdateFirmwarePresenter
      data={data}
      step={step}
      percent={percent}
      onClickCancel={onClickCancel}
      onClickManageAutoUpdate={onClickManageAutoUpdate}
      onClickUpdate={onClickUpdate}
      showReleaseNote={showReleaseNote}
      onModal={onModal}
      uploadStep={uploadStep}
      isLoadingUpdate={mutationFirmwareUpdate.loading}
      failedReason={errorReason}
      disabledUpdate={
        !mutationFirmwareUpdate.idle || !data?.isUpdate || !onlineStatus
      }
      onlineStatus={onlineStatus}
      showConfirmModal={showConfirmModal}
      onConfirmModal={onConfirmModal}
      autoUpdate={autoUpdate}
    />
  );
}
